import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { AppGlobal } from './app.global';

import { Actualite } from './actualite';

@Injectable()
export class ActualiteService {

  static readonly ACTUALITES: Array<Actualite> = [
    {
      url: 'loi-bourquin',
      titre: `Tout savoir sur la résiliation de votre assurance emprunteur`,
      resume: `Chaque année, à date anniversaire, vous pouvez changer votre assurance de prêt immobilier 
        et vous assurer ainsi une meilleure couverture et/ou un tarif moins élevé.`,
      article: `
        <p class="actualite-text text-justify">
          Lorsque vous déménagez, vous n'avez pas toujours le temps de comparer les assurances des emprunteurs, 
          vos préoccupations étant tournées sur l'obtention de votre prêt et sur la logistique du déménagement. 
          Plusieurs mois après, vous avez peut-être davantage de temps pour comparer les différentes compagnies 
          d'assurance, les différentes offres sur le marché, et demander plusieurs devis.
        </p>
        <p class="actualite-text text-justify">
          <strong>Grâce au nouveau dispositif législatif mis en place à travers l'amendement dit « Bourquin »</strong>, 
          depuis le 1er janvier 2018, il est possible de <strong>changer plus facilement 
          d'<span class="text-custom-primary">assurance prêt immobilier</span> (appelée aussi assurance emprunteur)</strong> 
          en substituant votre contrat initial par un nouveau contrat.
        </p>
        <p class="actualite-text actualite-subtitle">
          1. Qu'apporte réellement ce texte ?
        </p>
        <p class="actualite-text text-justify">
          Avant l'amendement Bourquin, il n'était possible de changer d'assurance emprunteur que pendant les 
          <strong>douze premiers mois suivants la signature</strong> de votre offre de prêt immobilier conformément 
          à la loi Hamon. Désormais, <strong>chaque année les emprunteurs peuvent <span class="text-custom-primary">
          changer d'assurance prêt immobilier</span> à la date d'anniversaire</strong> de leur offre de prêt ou de 
          leur contrat d'assurance emprunteur si ce dernier n'a pas été souscrit auprès de l'assurance de la banque, 
          sous réserve que leur assureur actuel en soit informé en respectant un préavis de deux mois (deux semaines 
          seulement si vous avez souscrit votre assurance de prêt depuis moins d'un an).
        </p>
        <p class="actualite-text text-justify">
          D'autre part, le nouveau contrat doit proposer un niveau de garanties identique ou supérieur à celui du 
          contrat résilié pour être accepté par la banque. C'est aussi l'occasion de <strong class="text-custom-primary">
          vérifier la bonne adéquation de son contrat d'assurance emprunteur</strong> avec sa situation, familiale et 
          professionnelle, d'aujourd'hui ; qui a pu considérablement varier depuis la date de signature de votre offre de prêt.
        </p>
        <p class="actualite-text actualite-subtitle">
          2. Qui est concerné ?
        </p>
        <p class="actualite-text text-justify">
          Cette nouvelle capacité donnée à tout assuré de renégocier son assurance de prêt à chaque date anniversaire concerne 
          <strong class="text-custom-primary">tous les particuliers ayant souscrit un crédit immobilier personnel ou professionnel 
          à usage d'habitation</strong>.
        </p>
        <p class="actualite-text actualite-subtitle">
          3. Quelle est la date anniversaire de votre assurance de prêt ?
        </p>
        <p class="actualite-text text-justify">
          La date d'anniversaire à laquelle il est possible de changer de contrat peut différer selon les cas. Si l'on a souscrit 
          l'assurance emprunteur proposée par la banque (assurance groupe), la date d'anniversaire correspond en général à la date 
          de signature de l'offre de prêt immobilier. Si l'on a souscrit une assurance en délégation lors de la signature du prêt 
          ou pendant la durée du prêt, <strong>c'est à la date anniversaire de la signature du contrat d'assurance qu'il est possible 
          de <span class="text-custom-primary">résilier son assurance prêt immobilier</span></strong>. 
        </p>
        <p class="actualite-text text-justify">
          Le mieux dans tous les cas est de prendre en compte un délai supplémentaire, car les démarches à suivre ne se feront pas 
          en quelques jours. 
        </p>
        <p class="actualite-text text-justify">
          Pensez donc à anticiper ce renouvellement en commençant à vous renseigner, voire à souscrire à un nouveau contrat bien plus 
          de 2 mois avant la date de reconduction annuelle qui est le délai minimum légal requis. Toutefois, ce délai se trouve réduit 
          à 15 jours si votre assurance de prêt a moins d'un an.
        </p>
        <p class="actualite-text actualite-subtitle">
          4. L'intérêt ?
        </p>
        <p class="actualite-text text-justify">
          Ce nouveau dispositif législatif mis en place à travers l'amendement dit « Bourquin » permet aux emprunteurs de réaliser 
          <strong>des économies sur le coût total de leur crédit immobilier en faisant jouer la concurrence</strong>. Les banques 
          proposent en effet traditionnellement des taux compris entre 0,25 et 0,50 % du capital emprunté alors que les assurances 
          individuelles affichent des tarifs à partir de 0,09 % du montant du crédit. Pourquoi ? Parce qu'elles se basent sur une 
          individualisation plus fine du calcul de risque, et donc, dans de nombreux cas, permettent de proposer un tarif plus adapté.
        </p>
        <p class="actualite-text text-justify">
          En cas de prêt souscrit il y a de nombreuses années, la revisite de votre assurance de prêt permet également de vous assurer 
          d'être correctement couvert, dans la mesure où votre situation tant personnelle que professionnelle a pu considérablement 
          évoluer depuis la date de signature de votre offre de prêt.
        </p>
      `,
      date: new Date(2018, 0, 1),
      wide: false
    },
    {
      url: 'renegocier-assurance',
      titre: `Renégocier son assurance emprunteur, comment ça marche ?`,
      resume: `Testez en quelques minutes les avantages d'un changement d'assurance pour votre prêt et souscrivez directement en 
        ligne ; nous vous guidons à chaque étape de la résiliation de votre contrat actuel.`,
      article: `
        <p class="actualite-text actualite-subtitle">
          Quelles sont les conditions à respecter pour renégocier son assurance de prêt immobilier ?
        </p>
        <ul class="list-unstyled actualite-list">
          <li class="text-justify">
            1)	Respecter un préavis de deux mois avant la date anniversaire afin d'informer votre assureur actuel. Cela signifie 
            que toutes vos démarches doivent avoir été effectuées avant ce délai, n'hésitez pas à anticiper.
          </li>
          <li class="text-justify">
            2)	Opter pour un nouveau contrat qui propose un niveau de garanties identique ou supérieur à celui du contrat résilié 
            pour être accepté par la banque.
          </li>
        </ul>
        <p class="actualite-text actualite-subtitle">
          Contrat groupe vs contrat individuel : pourquoi sont-ils si différents ?
        </p>
        <p class="actualite-text text-justify">
          Le contrat groupe est celui proposé par votre organisme prêteur. Il vise à mutualiser les risques et à proposer des tarifs 
          globalement homogènes. L'assurance proposée par votre banque est appelée assurance groupe. Pourquoi « groupe » ? Parce qu'il 
          s'agit d'une offre collective proposée par la banque à ses clients. Les risques y sont mutualisés. En clair, que vous soyez 
          par exemple fumeur ou non-fumeur, vous disposez du même taux d'assurance prêt immobilier.
        </p>
        <p class="actualite-text text-justify">
          A l'inverse, les contrats individuels, proposés par les assureurs, proposent une analyse de risque sur mesure (profession, 
          habitudes de vie…) et donc des tarifs beaucoup plus personnalisés. Cela ne signifie pas économie à chaque fois, mais dans 
          une grande partie des cas.
        </p>
        <p class="actualite-text actualite-subtitle">
          Comment se passe le changement de votre assurance de prêt immobilier ?
        </p>
        <p class="actualite-text text-justify">
          La résiliation de votre assurance emprunteur se fait simplement : vous devez envoyer, moyennant un préavis minimum de deux 
          mois avant la date anniversaire de votre offre de prêt ou de votre contrat d'assurance (15 jours si votre offre de prêt a 
          été souscrite depuis moins d'un an), un courrier recommandé avec accusé de réception à votre compagnie d'assurance actuelle 
          afin de l'informer de votre souhait de mettre fin à son contrat, en lui transmettant les éléments concernant votre prêt 
          immobilier, ainsi qu'en joignant l'attestation du nouveau contrat d'assurance auquel vous aurez souscrit par anticipation. 
          La plupart des assureurs peuvent vous accompagner dans ces démarches, voire réaliser en votre nom (via mandat) les démarches 
          de résiliation du contrat en cours.
        </p>
        <p class="actualite-text actualite-subtitle">
          Ma banque peut-elle refuser ma demande de substitution d'assurance ?
        </p>
        <p class="actualite-text text-justify">
          <strong>Votre banque ne peut pas refuser votre demande de substitution d'assurance de prêt immobilier</strong>. Seule 
          condition : les garanties de la nouvelle offre doivent être au moins identiques à celles proposées initialement par votre banque.
        </p>
        <p class="actualite-text text-justify">
          Si votre banque souhaite refuser votre délégation d'assurance, elle doit justifier sa décision par écrit. N'hésitez pas à vous 
          faire accompagner par des experts.
        </p>
        <p class="actualite-text text-center">
          <strong>Trouvez la meilleure assurance de prêt, c'est facile et gratuit !</strong>
        </p>
        <p class="actualite-text text-justify">
          Faites-vous votre propre opinion avant de choisir de faire appel à un professionnel et essayez par vous-même !
        </p>
        <p class="actualite-text text-justify">
          <a href="/formulaire/pret">Calculez en moins d'1 minute le tarif de votre nouvelle assurance et, en comparaison, l'économie que 
          vous êtes susceptible de réaliser sur votre assurance de prêt actuelle.</a>
        </p>
      `,
      date: new Date(2018, 1, 1),
      wide: true
    },
    {
      url: 'droit-resiliation',
      titre: `Le droit à la résiliation annuelle des contrats d'assurance des emprunteurs en 4 points-clef`,
      resume: `Le droit à la résiliation annuelle des contrats d'assurance des emprunteurs en 4 points-clef`,
      article: `
        <p class="actualite-text actualite-subtitle">
          1. Rappel de la position du Conseil constitutionnel
        </p>
        <p class="actualite-text text-justify">
          Dans sa communication du 12 janvier 2018, le Conseil constitutionnel a explicité <strong>le droit à la 
          résiliation annuelle</strong> de son assurance emprunteur : "Le législateur a entendu renforcer la protection 
          des consommateurs en assurant un meilleur équilibre contractuel entre l'assuré emprunteur, les établissements 
          bancaires et leurs partenaires actuels". Quant à l'application de ce principe au stock des contrats, "Le 
          législateur a voulu [...] que cette réforme puisse profiter au grand nombre des emprunteurs ayant déjà conclu 
          un contrat d'assurance collectif [...] poursuivant un objectif d'intérêt général"(1).
        </p>
        <p class="actualite-text actualite-subtitle">
          2. Le poids de l'assurance dans le coût total d'un crédit immobilier
        </p>
        <p class="actualite-text text-justify">
          La réforme va dans le sens d'une plus grande transparence sur les tarifs des assurances de prêt. En effet parfois 
          considérée comme secondaire par les emprunteurs, <strong>l'assurance de prêt représente en moyenne de 30 à 40 % 
          du coût global d'un crédit immobilier</strong>, en particulier dans un contexte de taux bas. Bien sûr, cela dépend 
          de chaque cas personnel, et il est donc important de comparer son contrat actuel, en particulier si c'est un contrat 
          Groupe, à des assurances individuelles afin de se faire sa propre opinion.
        </p>
        <p class="actualite-text actualite-subtitle">
          3. Qu'est-ce que le particulier a à y gagner ?
        </p>
        <p class="actualite-text text-justify">
          Le gain ne sera pas forcément financier, il peut concerner une meilleure couverture en permettant d'actualiser sa 
          situation, en particulier pour un couple (par exemple pour revoir la répartition de la couverture entre les deux 
          conjoints) ou dans le cas d'un changement d'activité. Dans tous les cas, une analyse précise de chaque situation 
          personnelle est nécessaire. Une renégociation de son assurance de prêt peut également permettre de faire valoir 
          son droit à l'oubli qui consiste, depuis 2017, à permettre aux personnes ayant été atteintes d'un cancer ou d'une 
          hépatite C sans rechute depuis 10 ans, de ne plus avoir à le préciser lors d'une demande d'assurance, ce qui permet 
          d'effacer en quelque sorte les surprimes liées potentiellement à leur ancienne maladie.
        </p>
        <p class="actualite-text actualite-subtitle">
          4. Comment s'y prendre ?
        </p>
        <p class="actualite-text text-justify">
          Réalisez votre demande de <a href="/formulaire/pret">devis en ligne</a> en quelques minutes afin
          d'obtenir une tarification correspondant exactement à votre situation et faites-vous votre propre
          opinion ! Essayez par vous-mêmes...
        </p>
        <p class="actualite-text text-justify">
          Mais attention, les <a href="/actualite/demarches-resiliation">démarches nécessaires</a> à une 
          renégociation de votre assurance de prêt peuvent prendre un certain temps. Renseignez-vous en amont.
        </p>
        <p class="actualite-text text-justify">
          (1) Propos recueillis par l'Argus de L'Assurance, 26 janvier 2018.
        </p>
      `,
      date: new Date(2018, 2, 1),
      wide: true
    },
    {
      url: 'demarches-resiliation',
      titre: `Les démarches à suivre pour résilier son assurance emprunteur`,
      resume: `La résiliation de votre contrat d'assurance emprunteur actuel doit passer par des étapes bien précises`,
      article: `
        <p class="actualite-text text-justify">
          La résiliation de votre contrat d'assurance emprunteur actuel doit passer par des étapes bien précises qu'il 
          faut avoir en tête avant de se lancer.
        </p>
        <p class="actualite-text text-justify">
          Vous devez tout d'abord rechercher un contrat qui respecte l'équivalence de garanties demandée par votre organisme 
          prêteur.
        </p>
        <p class="actualite-text text-justify">
          Vous devez ensuite réaliser un devis auprès de l'assureur sélectionné, remplir le questionnaire de santé demandé 
          par cet assureur et, le cas échéant, effectuer les examens médicaux nécessaires. A noter que les produits proposés 
          dans ce site vous offrent un parcours de sélection médicale en ligne qui vous permet, dans plus de 80% des cas, 
          d'obtenir une réponse immédiate sans attendre le résultat d'éventuelles formalités médicales.
        </p>
        <p class="actualite-text text-justify">
          Après obtention de votre certificat d'assurance, vous devez adresser votre demande de résiliation à votre banque en 
          recommandé avec accusé de réception, en joignant le certificat d'assurance ainsi que les conditions générales du 
          nouveau produit proposé par l'assureur de substitution.
        </p>
        <p class="actualite-text text-justify">
          La banque a alors 10 jours ouvrés pour vous répondre, sachant que le courrier doit lui être parvenu plus de 2 mois 
          avant la date anniversaire de votre contrat actuel ou de la signature de votre offre de prêt immobilier.
        </p>
        <p class="actualite-text text-justify">
          Prenez vos précautions et lancez vos démarches au moins 2 mois et demi avant cette date butoir, sous peine de devoir 
          refaire la démarche... un an après ! Et n'hésitez pas à vous entourer des conseils de professionnels.
        </p>
      `,
      date: new Date(2018, 2, 15),
      wide: false
    }
  ];

  constructor(private appGlobal: AppGlobal) {
    if (this.appGlobal.isAshlerManson()) {
      ActualiteService.ACTUALITES.find(t => t.url === 'loi-bourquin').titre = "Changer l’assurance de son prêt pour faire des économies !";
      ActualiteService.ACTUALITES.find(t => t.url === 'loi-bourquin').article = 
      `<p class="actualite-text text-justify">
          Pendant longtemps, il était impossible de changer d’assurance de prêt immobilier en cours de remboursement. Depuis 
          <u>l’adoption par le législateur des loi Hamon et Bourquin,</u>
          <strong> il est possible de changer son assurance de prêt sans préavis la première année et en respectant un préavis une fois par an pour les prêts ayant plus d’un an.</strong>, 
        </p>
        <p class="actualite-text actualite-subtitle">
          &nbsp;&nbsp;&nbsp;&nbsp;1. Le Changement d’assurance dans la première année de votre prêt ?
        </p>
          <p class="actualite-text text-justify">
            Souvent signées dans l’urgence pour obtenir le prêt immobilier et disposer des fonds nécessaires à l’achat immobilier, 
            les assurances sont peu, voire pas du tout négociées par les emprunteurs.
          </p>
          <p class="actualite-text text-justify">
            Pourtant, il existe un <strong>formidable levier d’économies en changeant d’assurance.</strong>
          </p>
          <p class="actualite-text text-justify">
            Afin de permettre aux emprunteurs de réaliser des économies,
            <u>le législateur autorise désormais un emprunteur à changer d’assurance dans les douze premiers mois suivant la signature de l’offre de prêt immobilier.</u>
          </p>
          <p class="actualite-text text-justify">
          Pas de formalisme particulier, il suffit d’envoyer à la banque, dans un courrier recommandé, 
          en indiquant vouloir bénéficier du dispositif HAMON, les nouveaux certificats d’assurance ainsi que la notice du nouveau contrat. (Préavis de 15 jours)
          </p>
          <p class="actualite-text text-justify">
          Par ailleurs, le <strong>nouveau contrat d’assurance Décès Invalidité doit comporter un niveau de garantie au moins équivalent à celui précédemment mis en place :</strong>
          </p>
          <p class="actualite-text text-justify"><ul>
            <li>Les quotités d’assurance doivent être <strong>au moins du même niveau</strong> que celles prévues (si les emprunteurs sont couverts chacun à 100 %, ils ne peuvent baisser les quotités.)</li>
            <li>Les <strong>garanties souscrites doivent être identiques</strong> : par exemple si l’Incapacité de travail a été souscrite, elle ne pourra pas être retirée dans le nouveau contrat.</li>
            <li>La nouvelle assurance <strong>ne doit pas comporter une exclusion</strong> qui était précédemment prise en charge.</li>
          </ul></p>
          <p class="actualite-text text-justify">En revanche, il est tout à fait possible de renforcer ses garanties en ajoutant une option qui n’était pas prévue ou encore en augmentant les quotités. </p>
        <p class="actualite-text actualite-subtitle">
          &nbsp;&nbsp;&nbsp;&nbsp;2.	Le changement d’assurance après la première année  ?
        </p>
        <p class="actualite-text text-justify">
          <u>La loi de 2018 permet à tous les assurés de changer d’assurance tous les ans. Et cela concerne tous les contrats d’assurance qui sont en vigueur.</u>
        </p>
        <p class="actualite-text text-justify">
          En préambule, il faut comprendre qu’il s’agit d’un nouveau contrat d’assurance emprunteur. Aussi, il sera peut-être nécessaire de devoir refaire des examens médicaux. Si jamais, 
          votre situation médicale s’est dégradée depuis votre première souscription, il ne sera probablement pas judicieux d’effectuer le changement de l’assurance. 
        </p>
        <p class="actualite-text text-justify">
          <strong>Pour changer d’assurance, il faudra anticiper vos démarches.</strong> En effet, vous devrez respecter un préavis de deux mois avant la date anniversaire. 
          Il existe un flou sur la date de départ du préavis. Pour être sûr de ne pas louper la date anniversaire, nous vous conseillons d’anticiper vos 
          démarches en sollicitant un changement d’assurance 3 mois avant la date de l’édition de l’offre de prêt. C’est-à-dire qu’il faudra aussi anticiper 
          la souscription de la nouvelle assurance.
        </p>
        <p class="actualite-text text-justify">
          <u>Exemple de calendrier :</u><br>
          Une offre de prêt a été signée le 01/01/2018.<br>
          Nous sommes le 01/03/2020. Vous pourrez donc théoriquement changer d’assurance le 01/01/2021.<br>
          <ul>
            <li>Vous devez engager vos démarches d’assurance en Septembre 2020 </li>
            <li>Avant le 1er Octobre 2020 vous devez retourner votre préavis sous forme recommandée à votre banque  en y joignant les conditions particulières et la notice de la nouvelle assurance.</li>
            <li>Votre banque fera la substitution au 01/01/2021. En clair l’ancienne assurance sera résiliée et la nouvelle prendra effet.</li>
          </ul>
        </p>

        <p class="actualite-text text-justify">
          En effet, la nouvelle prime d’assurance pourrait être plus chère car elle tiendrait compte de l’état de santé actuel.
        </p>
        <p class="actualite-text actualite-subtitle">
          &nbsp;&nbsp;&nbsp;&nbsp;3.	Quels sont les avantages à changer d’assurance ?
        </p>
        <p class="actualite-text actualite-subsubtitle">
          S’assurer pour moins cher 
        </p>
        <p class="actualite-text text-justify">
          Source de profit important pour les banques, l’<strong>assurance emprunteur est souvent imposée par les banques.</strong>
          L’assurance de banque est souvent une assurance dite « <strong>assurance groupe</strong> ».
        </p>
        <p class="actualite-text text-justify">
          C’est-à-dire que <strong>selon une classe d’âge, les assurés paieront la même cotisation, qu’ils se situent au début de la classe 
          d’âge de la tranche ou qu’il se situe à la fin (exemple classe d’âge 36-45 ans).</strong> Il y a une mutualisation. 
          Par ailleurs, les fumeurs paient un peu plus pour que les non-fumeurs payent un peu moins. 
        </p>
        <p class="actualite-text text-justify">
          De plus, <strong>la cotisation est souvent exprimée en fonction du capital initial.</strong> 
        </p>
        <p class="actualite-text text-justify">
          <u>L’assurance en délégation est dans la très grande majorité des cas une assurance individuelle.</u> 
        </p>
        <p class="actualite-text text-justify">
          <strong>L’assuré paie en fonction de sa situation et sur le capital restant dû.</strong> Il paie donc moins cher, 
          voire beaucoup moins cher surtout s’il est jeune, cadre, non-fumeur et en bonne santé.
        </p>
        <p class="actualite-text text-justify">
          <strong>Il peut espérer jusqu’à 70 % d’économies dans le meilleur des cas.</strong>
        </p>
        <p class="actualite-text text-justify">
          <u>Exemple :</u><br>
          Votre banque vous assure à 0.27 % sur 200.000 € pendant 20 ans : vous allez payer au total 10.800 € d’assurance.<br>
          Une compagnie vous assure pour 0.09 %. Vous allez payer au total dans ce cas : 3.600 €<br>
          <strong>Soit près de 7200 € d’économies</strong><br>
        </p>
        <p class="actualite-text actualite-subsubtitle">
          S’assurer mieux 
        </p>
        <p class="actualite-text text-justify">
          Les <strong>besoins en assurance évoluent</strong>, notamment en fonction des hausses ou baisses de salaires ou encore de changement professionnel.
        </p>
        <p class="actualite-text text-justify">
          Changer d’assurance permet aussi de faire le point et d’améliorer la couverture d’emprunt pour protéger au mieux sa famille. 
        </p>
        <p class="actualite-text text-justify">
          <strong>Notre conseil : Investir une partie du gain pour améliorer sa couverture d’assurance emprunteur.</strong>
        </p>`;
    }
    
  }

  getLastFourActualites(): Array<Actualite> {
    return ActualiteService.ACTUALITES.slice(0).sort(function(a1, a2) {
      return a2.date.getTime() - a1.date.getTime();
    }).slice(0, 4);
  }

  getActualiteFromUrl(url: string): Observable<Actualite> {
    return of(ActualiteService.ACTUALITES.find(a => a.url === url));
  }

  getActualites(): Array<Actualite> {
    return ActualiteService.ACTUALITES;
  }

}



// WEBPACK FOOTER //
// ./src/app/actualite.service.ts